import { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const LIGHTS_OFF =
  "/assets/homepage/hero-alt/lights-off-3200.jpg";
const LIGHTS_ON =
  "/assets/homepage/hero-alt/light-on-3200.jpg";

export default function Hero({ onMenuOpen, onBegin, onViewWork }) {
  const reducedMotion = useReducedMotion();

  // Animation phases
  const [entered, setEntered] = useState(false);
  const [lit, setLit] = useState(false);
  const [descriptorOut, setDescriptorOut] = useState(false);
  const [brandTop, setBrandTop] = useState(false);
  const [navVisible, setNavVisible] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [offReady, setOffReady] = useState(false);
  const [onReady, setOnReady] = useState(false);

  // Preload hero images. Set ready flags as soon as each is decoded.
  useEffect(() => {
    let cancelled = false;
    const preload = (src, setReady) => {
      const img = new Image();
      img.decoding = "async";
      img.fetchPriority = "high";
      img.onload = () => {
        if (cancelled) return;
        if (img.decode) img.decode().catch(() => {}).finally(() => setReady(true));
        else setReady(true);
      };
      img.onerror = () => !cancelled && setReady(true); // don't block forever
      img.src = src;
    };
    preload(LIGHTS_OFF, setOffReady);
    preload(LIGHTS_ON, setOnReady);
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      // Settled state immediately — no animation.
      setEntered(true);
      setLit(true);
      setDescriptorOut(true);
      setBrandTop(true);
      setNavVisible(true);
      setRevealed(true);
      return;
    }

    const timers = [];
    // 0.5s — brand name begins fading in
    timers.push(setTimeout(() => setEntered(true), 500));
    // 2.6s — descriptor begins fading OUT (after the hold)
    timers.push(setTimeout(() => setDescriptorOut(true), 2600));
    // 2.9s — wordmark begins moving upward into nav (descriptor finishes fading at ~3.0s)
    timers.push(setTimeout(() => setBrandTop(true), 2900));
    // 3.2s — start slow lights crossfade
    timers.push(setTimeout(() => setLit(true), 3200));
    // 5.2s — nav links fade in (after wordmark settles into nav)
    timers.push(setTimeout(() => setNavVisible(true), 5200));
    // 7.8s — final hero caption fades in (after lights have breathed)
    timers.push(setTimeout(() => setRevealed(true), 7800));

    return () => timers.forEach(clearTimeout);
  }, [reducedMotion]);

  return (
    <section
      className={[
        "hero",
        offReady ? "off-ready" : "",
        (lit && onReady) || reducedMotion ? "is-lit" : "",
        revealed ? "is-revealed" : "",
      ].join(" ")}
      data-testid="hero-section"
      aria-label="Ōraṇi — opening scene"
    >
      {/* Background image layers */}
      <div
        className="hero__layer hero__layer--off"
        role="img"
        aria-label="Atmospheric interior setting before the lights come on"
        style={{ backgroundImage: `url(${LIGHTS_OFF})` }}
        data-testid="hero-layer-off"
      />
      <div
        className="hero__layer hero__layer--on"
        role="img"
        aria-label="Warm traditional-modern interior with layered lighting, patterned panels, and material detail"
        style={{ backgroundImage: `url(${LIGHTS_ON})` }}
        data-testid="hero-layer-on"
      />

      {/* Atmospheric gradient overlay */}
      <div className="hero__overlay" aria-hidden="true" />
      {/* Intro vignette dissolves once lit */}
      <div className="hero__intro-vignette" aria-hidden="true" />
      {/* Subtle cinematic grain (suppressed on mobile + reduced motion) */}
      <div className="hero__grain" aria-hidden="true" />

      {/* Brand mark — animates from screen center to top nav position */}
      <div
        className={[
          "brand-mark",
          entered ? "is-entered" : "",
          descriptorOut ? "descriptor-out" : "",
          brandTop ? "is-top" : "",
        ].join(" ")}
        data-testid="brand-mark"
        data-state={brandTop ? "top" : "center"}
      >
        <div className="brand-mark__inner">
          <h1 className="brand-mark__name brand-wordmark" data-testid="brand-name">
            Ōraṇi
          </h1>
        </div>
        <p className="brand-mark__sub" data-testid="brand-sub">
          Interiors, Architecture &amp; Experience Design
        </p>
      </div>

      {/* Top navigation */}
      <nav
        className={["site-nav", navVisible ? "is-visible" : ""].join(" ")}
        aria-label="Primary"
        data-testid="site-nav"
      >
        <ul className="site-nav__list site-nav__list--left">
          <li>
            <a href="#philosophy" onClick={(e) => { e.preventDefault(); onViewWork && onViewWork(); }} data-testid="nav-work">Work</a>
          </li>
          <li>
            <a href="#philosophy" onClick={(e) => { e.preventDefault(); onViewWork && onViewWork(); }} data-testid="nav-process">Process</a>
          </li>
        </ul>

        <div className="site-nav__brand-slot" aria-hidden="true" />

        <ul className="site-nav__list site-nav__list--right">
          <li>
            <a href="#philosophy" onClick={(e) => { e.preventDefault(); onViewWork && onViewWork(); }} data-testid="nav-studio">Studio</a>
          </li>
          <li>
            <a href="#philosophy" onClick={(e) => { e.preventDefault(); onBegin && onBegin(); }} data-testid="nav-contact">Contact</a>
          </li>
          <li>
            <button
              type="button"
              className="site-nav__cta"
              onClick={onBegin}
              data-testid="nav-start"
              aria-label="Start a conversation"
            >
              Start a Conversation
            </button>
          </li>
        </ul>

        <button
          type="button"
          className="site-nav__menu-btn"
          onClick={onMenuOpen}
          aria-label="Open navigation menu"
          data-testid="mobile-menu-toggle"
        >
          <Menu size={18} strokeWidth={1.4} />
        </button>
      </nav>

      {/* Hero copy — quiet caption, lower-center */}
      <div className="hero__copy" data-testid="hero-copy">
        <h2 className="hero__headline" data-testid="hero-headline">
          Homes composed in <em className="hero__headline-em">light</em>, detail, and drama.
        </h2>
        <button
          type="button"
          className="cta-explore"
          onClick={onViewWork}
          data-testid="cta-explore"
          aria-label="Explore the studio's philosophy"
        >
          <span>Explore</span>
          <svg className="cta-explore__arrow" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M7 1.5v11M3 8.5L7 12.5L11 8.5" stroke="currentColor" strokeWidth="1" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {/* Quiet scroll hint */}
      <div className="hero__scroll-hint" aria-hidden="true" data-testid="hero-scroll-hint">
        Scroll
      </div>
    </section>
  );
}
